import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ArenaEvent,
  ArenaResponse,
  ArenaSnapshot,
  Choice,
  Leader,
  Round,
  OddsSelection,
  LockedOdds,
  MarketUpdate,
} from '@pons/contracts';
import { accountFetch } from './auth/api';
import { mergeMarket, retainAcceptedPredictions } from './market';
const PROFILE_KEY = 'pons-profile-name';
export function useArena(accountId?: string) {
  const profileKey = accountId ? `${PROFILE_KEY}:${accountId}` : PROFILE_KEY;
  const [data, setData] = useState<{ rounds: Round[]; name: string }>({
    rounds: [],
    name: 'Predictor',
  });
  const [personal, setPersonal] = useState<Round[]>([]),
    [leaders, setLeaders] = useState<Leader[]>([]);
  const [now, setNow] = useState(Date.now),
    [ready, setReady] = useState(false),
    [mode, setMode] = useState('live');
  const [notice, setNotice] = useState(''),
    [syncError, setSyncError] = useState(''),
    [storageError, setStorageError] = useState(false),
    [catchingUp, setCatchingUp] = useState(false),
    [connection, setConnection] = useState('connecting'),
    [marketStatus, setMarketStatus] = useState('recovering');
  const offset = useRef(0),
    inFlight = useRef(false),
    lastPush = useRef(0),
    pending = useRef(new Set<string>());
  const accepted = useRef(new Map<string, Round>());
  const arena = useRef<ArenaSnapshot | null>(null),
    sequence = useRef(-1),
    personalRevision = useRef(0),
    refreshingMarket = useRef<MarketUpdate[] | null>(null),
    streamReady = useRef(false),
    generation = useRef(0);
  const apply = useCallback((body: ArenaSnapshot) => {
    body = retainAcceptedPredictions(body, accepted.current);
    arena.current = body;
    offset.current = body.serverTime - Date.now();
    setData((previous) => ({ ...previous, rounds: body.rounds }));
    setPersonal(body.predictions);
    setLeaders(body.ranking);
    setSyncError(body.syncError);
    setMarketStatus(body.marketStatus || 'recovering');
    setCatchingUp(
      !!body.headBlock && body.headBlock - (body.indexedBlock || 0) > 500,
    );
    setMode('live');
    setReady(true);
  }, []);
  const refresh = useCallback(
    async (signal?: AbortSignal) => {
      if (inFlight.current) return;
      inFlight.current = true;
      const startedGeneration = generation.current,
        startedPersonalRevision = personalRevision.current,
        marketUpdates: MarketUpdate[] = [];
      refreshingMarket.current = marketUpdates;
      // Bound both a stalled request and the streamed deltas waiting to reconcile it.
      const deadline = new AbortController();
      const timeout = setTimeout(() => deadline.abort(), 10_000);
      try {
        const response = await accountFetch(accountId, '/api/tokens', {
          cache: 'no-store',
          signal: signal
            ? AbortSignal.any([signal, deadline.signal])
            : deadline.signal,
        });
        if (!response.ok) throw Error('Data service temporarily unavailable.');
        const body = (await response.json()) as ArenaResponse;
        // A new connection or private snapshot supersedes this request in full.
        if (
          signal?.aborted ||
          startedGeneration !== generation.current ||
          startedPersonalRevision !== personalRevision.current
        )
          return body.mode;
        if (body.mode === 'mock') {
          setMode('mock');
          setReady(true);
          return 'mock';
        }
        // HTTP can contain fresh private votes even while public prices keep arriving.
        // Replay only the intervening public changes, preserving that personal data
        // without rolling prices, canonicality or the public roster backwards.
        apply(marketUpdates.reduce(mergeMarket, body));
        return body.mode;
      } catch (error) {
        if (
          !signal?.aborted &&
          startedGeneration === generation.current &&
          startedPersonalRevision === personalRevision.current
        ) {
          setSyncError(
            deadline.signal.aborted
              ? 'Data service request timed out. Retrying connection.'
              : error instanceof Error
                ? error.message
                : 'Connection unavailable.',
          );
          setReady(true);
        }
      } finally {
        clearTimeout(timeout);
        refreshingMarket.current = null;
        inFlight.current = false;
      }
    },
    [apply, accountId],
  );
  const setName = useCallback(
    (name: string) => {
      setData((previous) => ({ ...previous, name }));
      try {
        localStorage.setItem(profileKey, name);
      } catch {
        setStorageError(true);
      }
    },
    [profileKey],
  );
  useEffect(() => {
    try {
      const name = localStorage.getItem(profileKey);
      if (name) setData((previous) => ({ ...previous, name }));
    } catch {
      setStorageError(true);
    }
    const controller = new AbortController();
    let events: EventSource | undefined;
    void refresh(controller.signal).then((mode) => {
      if (controller.signal.aborted || mode === 'mock') return;
      events = new EventSource(
        accountId
          ? '/api/events?account=' + encodeURIComponent(accountId)
          : '/api/events',
      );
      events.onopen = () => {
        generation.current++;
        refreshingMarket.current = null;
        sequence.current = -1;
        streamReady.current = false;
        setConnection('connected');
      };
      events.onerror = () => {
        setConnection('reconnecting');
        streamReady.current = false;
      };
      events.onmessage = (event) => {
        try {
          const body = JSON.parse(event.data) as ArenaEvent;
          const nextSequence = body.sequence ?? 0;
          if (nextSequence <= sequence.current) return;
          if ('type' in body && body.type === 'market') {
            if (!streamReady.current || !arena.current) {
              void refresh(controller.signal);
              return;
            }
            sequence.current = nextSequence;
            refreshingMarket.current?.push(body);
            apply(mergeMarket(arena.current, body));
            // Sequence numbers also cover private batches sent to other users.
            // TCP preserves this connection's event order; reconnect supplies a full baseline.
          } else if ('mode' in body) {
            sequence.current = nextSequence;
            personalRevision.current++;
            refreshingMarket.current = null;
            streamReady.current = true;
            apply(body);
          }
          lastPush.current = Date.now();
          setConnection('connected');
        } catch {
          void refresh(controller.signal);
        }
      };
    });
    const fallback = setInterval(() => {
      if (Date.now() - lastPush.current > 15_000) {
        setConnection('reconnecting');
        void refresh(controller.signal);
      }
    }, 10_000);
    const clock = setInterval(() => setNow(Date.now() + offset.current), 250);
    return () => {
      controller.abort();
      events?.close();
      clearInterval(fallback);
      clearInterval(clock);
    };
  }, [apply, refresh, profileKey, accountId]);
  const submit = useCallback(
    async (id: string, choice: Choice, odds?: OddsSelection) => {
      if (pending.current.has(id)) return false;
      pending.current.add(id);
      try {
        const response = await accountFetch(accountId, '/api/predictions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id, choice, ...(odds ? { odds } : {}) }),
        });
        const body = (await response.json()) as {
          error?: string;
          createdAt: number;
          lockedOdds?: LockedOdds;
        };
        if (!response.ok)
          throw Error(body.error || 'Prediction could not be saved.');
        if (arena.current) {
          const rounds = arena.current.rounds.map((r) =>
            r.id === id
              ? {
                  ...r,
                  choice,
                  chosenAt: body.createdAt,
                  lockedOdds: body.lockedOdds,
                }
              : r,
          );
          const chosen = rounds.find((r) => r.id === id);
          if (chosen) {
            accepted.current.set(id, chosen);
            if (accepted.current.size > 100)
              accepted.current.delete(accepted.current.keys().next().value!);
          }
          apply({
            ...arena.current,
            rounds,
            predictions: chosen
              ? [
                  chosen,
                  ...arena.current.predictions.filter((r) => r.id !== id),
                ]
              : arena.current.predictions,
          });
        }
        setNotice(
          body.lockedOdds
            ? 'Prediction locked at ' + body.lockedOdds.coefficient + 'x.'
            : 'Prediction locked. You’re in.',
        );
        await refresh();
        return true;
      } catch (error) {
        void refresh();
        setNotice(
          error instanceof Error
            ? error.message
            : 'Prediction could not be saved.',
        );
        return false;
      } finally {
        pending.current.delete(id);
      }
    },
    [apply, refresh, accountId],
  );
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(''), 5000);
    return () => clearTimeout(timer);
  }, [notice]);
  return {
    data,
    personal,
    leaders,
    now,
    ready,
    mode,
    notice,
    syncError,
    storageError,
    catchingUp,
    connection,
    marketStatus,
    setName,
    submit,
  };
}
